import moment from 'moment';
import i18n from 'i18n';
import { numberWithCommas } from 'utils/StringUtil';
import { DeviceBusinessHoursProvenance } from 'core/deviceManagement/DeviceManagement';
import { NO_VALUE } from './deviceDetail';

export const AVAILABILITY_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * 可售率的顯示。
 *
 * 後端給的是 0 到 1 的分數，畫面上寫的是百分比，所以這裡要乘 100。
 * 進度條那一側剛好相反 —— 共用的 Progress 元件吃的就是 0 到 1，直接把原值給它。
 *
 * 沒有值時寫「—」而不是算下去：`null * 100` 是 0（畫成「0.0%」，等於宣稱全賣光了），
 * `undefined * 100` 是 NaN（畫成「NaN%」）。兩種都是假話，而且都不會報錯。
 * 真正的防線在 ws 層（缺席的數字在那裡就折成 null），這裡是最後一道。
 */
export function formatAvailabilityRate(
  rate: number | null | undefined
): string {
  if (rate === null || rate === undefined) {
    return NO_VALUE;
  }
  return `${(rate * 100).toFixed(1)}%`;
}

/**
 * 秒數的千分位，後面接單位。
 *
 * 用 numberWithCommas 而不是 getNumberAccurateToDecimalPlace：後者會無條件進位，
 * 秒數是已經算好的整數，進位只會讓它跟後端對不起來。
 *
 * 單位跟著數字走，不只放在欄位標題：可售與已售兩個數字會跟可售率、點位數並排，
 * 只看數字分不出哪一個是秒、哪一個是台數。
 *
 * 沒有值時寫「—」而不是「0 秒」：那兩件事對業務的意思相反。
 */
export function formatSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) {
    return NO_VALUE;
  }
  return i18n.t('deviceManagement.labels.secondsValue', {
    value: numberWithCommas(seconds)
  });
}

/**
 * 這一列算不算得出可售狀況。
 *
 * 只有 MISSING 算不出來（秒數與可售率全是 null）。CLOSED_ALL_WEEK 是算得出來的，
 * 答案是可售 0 秒，要照數字顯示，不能歸到「資料不足」那一邊。
 */
export function hasResolvedBusinessHours(
  provenance: DeviceBusinessHoursProvenance
): boolean {
  return provenance !== DeviceBusinessHoursProvenance.MISSING;
}

/** 走期共幾天，頭尾都算。 */
export function countDaysInRange(startDate: string, endDate: string): number {
  const start = moment(startDate, AVAILABILITY_DATE_FORMAT).startOf('day');
  const end = moment(endDate, AVAILABILITY_DATE_FORMAT).startOf('day');
  const days = end.diff(start, 'days') + 1;
  return days > 0 ? days : 0;
}
